import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import type {
  BookmarkPresetFilter,
  BookmarkQuery,
  BookmarkSecurityFilter,
  BookmarkSortField,
  BookmarkSortOrder,
} from '../../../types/bookmark'

/**
 * 북마크 필터 상태를 URL 쿼리로 관리한다.
 *
 * 기존에는 전부 useState 라 새로고침·뒤로가기·링크 공유 시 필터가 사라졌다.
 * URL 을 단일 진실 공급원으로 삼아 상태 동기화 문제도 함께 없앤다.
 */

const SORT_FIELDS: BookmarkSortField[] = [
  'updatedAt',
  'createdAt',
  'clickCount',
  'title',
  'originalUrl',
]
const SECURITY_FILTERS: BookmarkSecurityFilter[] = [
  'password',
  'expiring',
  'expired',
]
const PRESET_FILTERS: BookmarkPresetFilter[] = [
  'idle',
  'top',
  'untagged',
  'recent',
]
const ALLOWED_LIMITS = [10, 20, 50, 100]
/** 페이지당 개수는 기기별 취향이라 URL 이 아니라 기기에 남긴다 */
const PAGE_SIZE_STORAGE_KEY = 'bookmark-page-size'

const DEFAULTS = {
  sortBy: 'updatedAt' as BookmarkSortField,
  sortOrder: 'desc' as BookmarkSortOrder,
  page: 1,
  limit: 20,
}

type ParamPatch = Record<string, string | null>

function parseSortField(value: string | null): BookmarkSortField {
  return SORT_FIELDS.includes(value as BookmarkSortField)
    ? (value as BookmarkSortField)
    : DEFAULTS.sortBy
}

function parseSortOrder(value: string | null): BookmarkSortOrder {
  return value === 'asc' ? 'asc' : DEFAULTS.sortOrder
}

/** 허용된 보안 필터 값만 통과시킨다 (사이드바 등 외부 입력 정규화에도 사용) */
export function parseSecurityFilter(
  value: string | null
): BookmarkSecurityFilter | null {
  return SECURITY_FILTERS.includes(value as BookmarkSecurityFilter)
    ? (value as BookmarkSecurityFilter)
    : null
}

/** 허용된 저장 필터 값만 통과시킨다 */
export function parsePresetFilter(
  value: string | null
): BookmarkPresetFilter | null {
  return PRESET_FILTERS.includes(value as BookmarkPresetFilter)
    ? (value as BookmarkPresetFilter)
    : null
}

function parsePositiveInt(value: string | null, fallback: number): number {
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback
}

function allowedLimit(value: string | null, fallback: number): number {
  const parsed = parsePositiveInt(value, fallback)
  return ALLOWED_LIMITS.includes(parsed) ? parsed : fallback
}

/** 저장해 둔 페이지당 개수. 저장소를 못 쓰면 기본값을 쓴다. */
function storedPageSize(): number {
  try {
    return allowedLimit(
      localStorage.getItem(PAGE_SIZE_STORAGE_KEY),
      DEFAULTS.limit
    )
  } catch {
    return DEFAULTS.limit
  }
}

function rememberPageSize(limit: number): void {
  try {
    localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(limit))
  } catch {
    // 저장 실패는 화면 동작에 영향을 주지 않는다
  }
}

/** URL 값이 먼저, 없으면 기기에 저장해 둔 값 */
function parseLimit(value: string | null): number {
  return allowedLimit(value, storedPageSize())
}

function parseTags(value: string | null): string[] {
  if (!value) {
    return []
  }
  return value
    .split(',')
    .map(tag => tag.trim())
    .filter(Boolean)
}

export interface BookmarkFilters extends BookmarkQuery {
  folderId: string | null
  tags: string[]
  search: string
  favorite: boolean
  security: BookmarkSecurityFilter | null
  preset: BookmarkPresetFilter | null
  page: number
  limit: number
  sortBy: BookmarkSortField
  sortOrder: BookmarkSortOrder
}

export interface UseBookmarkFiltersResult {
  filters: BookmarkFilters
  hasActiveFilters: boolean
  selectFolder: (folderId: string | null) => void
  toggleFavorites: () => void
  toggleTag: (tagId: string) => void
  clearTags: () => void
  setSecurity: (security: BookmarkSecurityFilter | null) => void
  setPreset: (preset: BookmarkPresetFilter | null) => void
  changeSearch: (search: string) => void
  changeSort: (sortBy: BookmarkSortField) => void
  changePage: (page: number) => void
  changeLimit: (limit: number) => void
  clearFilters: () => void
}

export function useBookmarkFilters(): UseBookmarkFiltersResult {
  const [searchParams, setSearchParams] = useSearchParams()

  const filters = useMemo<BookmarkFilters>(
    () => ({
      folderId: searchParams.get('folder'),
      tags: parseTags(searchParams.get('tags')),
      search: searchParams.get('q') ?? '',
      favorite: searchParams.get('favorite') === 'true',
      security: parseSecurityFilter(searchParams.get('security')),
      preset: parsePresetFilter(searchParams.get('preset')),
      page: parsePositiveInt(searchParams.get('page'), DEFAULTS.page),
      limit: parseLimit(searchParams.get('limit')),
      sortBy: parseSortField(searchParams.get('sort')),
      sortOrder: parseSortOrder(searchParams.get('order')),
    }),
    [searchParams]
  )

  // 값이 null 인 키는 제거해 기본값이 URL 을 더럽히지 않게 한다
  const patchParams = useCallback(
    (patch: ParamPatch) => {
      setSearchParams(
        previous => {
          const next = new URLSearchParams(previous)
          Object.entries(patch).forEach(([key, value]) => {
            if (value === null) {
              next.delete(key)
            } else {
              next.set(key, value)
            }
          })
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  /** 목록의 범위를 바꾸는 필터들 — 하나를 고르면 나머지는 해제한다 */
  const resetScope: ParamPatch = {
    folder: null,
    tags: null,
    favorite: null,
    security: null,
    preset: null,
    page: null,
  }

  const selectFolder = useCallback(
    (folderId: string | null) =>
      patchParams({ ...resetScope, folder: folderId }),
    [patchParams]
  )

  const toggleFavorites = useCallback(
    () =>
      patchParams({
        ...resetScope,
        favorite: filters.favorite ? null : 'true',
      }),
    [patchParams, filters.favorite]
  )

  const toggleTag = useCallback(
    (tagId: string) => {
      const next = filters.tags.includes(tagId)
        ? filters.tags.filter(id => id !== tagId)
        : [...filters.tags, tagId]
      patchParams({
        folder: null,
        favorite: null,
        page: null,
        tags: next.length > 0 ? next.join(',') : null,
      })
    },
    [patchParams, filters.tags]
  )

  const clearTags = useCallback(
    () => patchParams({ tags: null, page: null }),
    [patchParams]
  )

  const setSecurity = useCallback(
    (security: BookmarkSecurityFilter | null) =>
      patchParams({ ...resetScope, security }),
    [patchParams]
  )

  const setPreset = useCallback(
    (preset: BookmarkPresetFilter | null) =>
      patchParams({ ...resetScope, preset }),
    [patchParams]
  )

  const changeSearch = useCallback(
    (search: string) =>
      patchParams({ q: search.trim() ? search : null, page: null }),
    [patchParams]
  )

  const changeSort = useCallback(
    (sortBy: BookmarkSortField) => {
      const nextOrder: BookmarkSortOrder =
        filters.sortBy === sortBy && filters.sortOrder === 'desc'
          ? 'asc'
          : 'desc'
      patchParams({
        sort: sortBy === DEFAULTS.sortBy ? null : sortBy,
        order: nextOrder === DEFAULTS.sortOrder ? null : nextOrder,
        page: null,
      })
    },
    [patchParams, filters.sortBy, filters.sortOrder]
  )

  const changePage = useCallback(
    (page: number) =>
      patchParams({ page: page > DEFAULTS.page ? String(page) : null }),
    [patchParams]
  )

  const changeLimit = useCallback(
    (limit: number) => {
      rememberPageSize(limit)
      patchParams({ limit: String(limit), page: null })
    },
    [patchParams]
  )

  const clearFilters = useCallback(
    () => patchParams({ ...resetScope, q: null }),
    [patchParams]
  )

  const hasActiveFilters =
    !!filters.folderId ||
    filters.tags.length > 0 ||
    !!filters.search ||
    filters.favorite ||
    !!filters.security ||
    !!filters.preset

  return {
    filters,
    hasActiveFilters,
    selectFolder,
    toggleFavorites,
    toggleTag,
    clearTags,
    setSecurity,
    setPreset,
    changeSearch,
    changeSort,
    changePage,
    changeLimit,
    clearFilters,
  }
}

export default useBookmarkFilters
